import Calculator from './Calculator.js';

/**
 * CustomCalculator - Calculator for custom volume input
 *
 * When user already knows the volume in liters or square meters
 *
 * @class CustomCalculator
 * @extends Calculator
 */
class CustomCalculator extends Calculator {
    /**
     * @param {Object} dimensions
     * @param {number} dimensions.volume - Volume in liters (objętość w litrach)
     * @param {number} [dimensions.drainageVolume=0] - Custom drainage volume if known
     * @param {number} [dimensions.drainageRatio=0.15] - Drainage ratio (default 15%)
     * @param {boolean} [withDrainage=true] - Whether to include drainage (from constructor param, not dimensions)
     */
    constructor(dimensions, withDrainage = true) {
        super(dimensions);
        const { volume = 0, drainageVolume = 0, drainageRatio = 0.15 } = dimensions;

        this.volume = parseFloat(volume) || 0;
        this.withDrainage = withDrainage;
        this.drainageVolume = parseFloat(drainageVolume) || 0;
        this.drainageRatio = drainageRatio;
    }

    /**
     * Calculate substrate volume for custom input
     * When user knows total volume, subtract drainage to get substrate volume
     * Total volume = substrate + drainage
     *
     * @returns {number} Substrate volume in liters
     */
    calculateSubstrateVolume() {
        if (!this.withDrainage) {
            return this.volume;
        }

        const drainageVolume = this.calculateDrainageVolume();
        return this.volume - drainageVolume;
    }

    /**
     * Calculate drainage volume for custom input
     * When user knows volume and wants drainage, calculate as ratio of volume
     * Default ratio is 15% unless custom drainageVolume is specified
     *
     * @returns {number} Drainage volume in liters
     */
    calculateDrainageVolume() {
        if (!this.withDrainage) {
            return 0;
        }

        if (this.drainageVolume > 0) {
            return this.drainageVolume;
        }

        // Default 15% drainage ratio
        return this.volume * this.drainageRatio;
    }

    /**
     * Set dimensions
     *
     * @param {Object} dimensions
     */
    setDimensions(dimensions) {
        Object.assign(this.dimensions, dimensions);
        if (dimensions.volume !== undefined) this.volume = parseFloat(dimensions.volume) || 0;
        if (dimensions.withDrainage !== undefined) this.withDrainage = dimensions.withDrainage;
        if (dimensions.drainageVolume !== undefined) this.drainageVolume = parseFloat(dimensions.drainageVolume) || 0;
        if (dimensions.drainageRatio !== undefined) this.drainageRatio = dimensions.drainageRatio;
    }

    /**
     * Validate dimensions
     *
     * @returns {Object} Validation result with isValid and errors array
     */
    validate() {
        const errors = [];

        if (this.volume <= 0) {
            errors.push('Objętość musi być większa od 0');
        }

        // Note: drainageVolume can now be larger than volume since they're calculated independently
        // Substrate gets 20% surplus, drainage is calculated separately

        return {
            isValid: errors.length === 0,
            errors
        };
    }
}

export default CustomCalculator;